/**
 * Registrar Display Utility
 *
 * Formats raw WHOIS/RDAP registrar values for display. Nominet-style registries
 * (.uk) publish registrar values as "Gandi [Tag = GANDI]", where only the IPS
 * tag identifies the registrar. The backend normalizes new scans down to just
 * the tag (see backend/pkg/lib/whois/whois.go `NormalizeRegistrar`), but legacy
 * rows persisted before that change still carry the full "name [Tag = X]"
 * string, so the UI formats at display time to keep both consistent.
 *
 * This mirrors the backend semantics exactly: an empty value stays empty, a
 * value ending in a "[Tag = X]" group collapses to the tag, an empty tag falls
 * back to the name portion, and any value without the tag notation passes
 * through trimmed. The IP-seeds column reuses the same field for ISP names, so
 * plain values ("Comcast Cable Communications, LLC") pass through unchanged.
 *
 * Backend Authority: backend/pkg/lib/whois/whois.go `NormalizeRegistrar`
 */

import {
  buildMergedFilterGroups,
  expandMergedFilterValues,
} from '@/utils/mergeFilterOptions.util';

/**
 * ASCII-only whitespace class used to build {@link NOMINET_TAG_PATTERN}. It
 * intentionally mirrors Go RE2's `\s`, which matches EXACTLY `[\t\n\f\r ]` —
 * not the wider set JavaScript's `\s` matches (NBSP U+00A0, vertical tab
 * `\v`, and other Unicode whitespace). Building the pattern from this class
 * (instead of JS `\s`) keeps the frontend's tag normalization byte-identical
 * to the backend authority (`nominetTagPattern` / `NormalizeRegistrar` in
 * backend/pkg/lib/whois/whois.go): Unicode whitespace INSIDE the tag notation
 * (e.g. "Gandi [<NBSP>tag = GANDI]") does NOT count as whitespace, so the
 * value fails to match and passes through unformatted — exactly as the backend
 * stores it raw. The outer value trim in {@link formatRegistrar} deliberately
 * stays `String.prototype.trim()` (Unicode), which matches Go's
 * `strings.TrimSpace` (also Unicode).
 */
const ASCII_WHITESPACE = '[\\t\\n\\f\\r ]';

/**
 * Matches a Nominet-style IPS tag suffix on a registrar value, e.g.
 * "Gandi [Tag = GANDI]": a name portion followed by a bracketed "[Tag = X]"
 * group (case-insensitive keyword, ASCII whitespace) at the END of the string.
 * Mirrors the backend `nominetTagPattern`, including its ASCII-only `\s`
 * semantics (see {@link ASCII_WHITESPACE}).
 */
const NOMINET_TAG_PATTERN = new RegExp(
  `^(.*?)${ASCII_WHITESPACE}*\\[${ASCII_WHITESPACE}*tag${ASCII_WHITESPACE}*=${ASCII_WHITESPACE}*([^\\]]*)\\]$`,
  'i'
);

/**
 * Upper bound on input length before {@link NOMINET_TAG_PATTERN} runs.
 * The pattern is quadratic worst-case under V8 regex backtracking, and table
 * cells call {@link formatRegistrar} with UNCAPPED, attacker-influenced WHOIS
 * values (the filter path caps input at <100 chars, but the cell path does
 * not). Real Nominet registrar strings are far shorter than this; anything
 * longer is a pathological value and passes through unformatted.
 */
const MAX_FORMAT_LENGTH = 256;

/**
 * Formats a raw WHOIS registrar value for display, collapsing Nominet-style
 * "name [Tag = X]" values to just the IPS tag.
 *
 * Known parity edge: {@link String.prototype.trim} strips U+FEFF (BOM) while Go's
 * `strings.TrimSpace` does not, so a BOM-only value yields `null` here but passes
 * through unchanged on the backend — an accepted divergence, unreachable for real
 * WHOIS values.
 *
 * @param value - Raw registrar (or ISP) value from the backend
 * @returns The formatted registrar, or null when there is nothing to show
 *   (callers render '-')
 *
 * @example
 * ```typescript
 * formatRegistrar('Gandi [Tag = GANDI]')      // → 'GANDI'
 * formatRegistrar('[ TAG = MARKMONITOR ]')    // → 'MARKMONITOR'
 * formatRegistrar('Foo [Tag = ]')             // → 'Foo' (empty tag falls back to name)
 * formatRegistrar('MarkMonitor Inc.')         // → 'MarkMonitor Inc.' (no tag notation)
 * formatRegistrar('  ')                        // → null
 * formatRegistrar(null)                        // → null
 * ```
 */
export function formatRegistrar(
  value: string | null | undefined
): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  // Defense against pathological WHOIS values: skip the quadratic regex for
  // over-long inputs and pass them through unformatted (see MAX_FORMAT_LENGTH).
  if (trimmed.length > MAX_FORMAT_LENGTH) {
    return trimmed;
  }

  const match = NOMINET_TAG_PATTERN.exec(trimmed);
  if (match === null) {
    return trimmed;
  }

  const tag = match[2].trim();
  if (tag !== '') {
    return tag;
  }

  // Empty tag ("Foo [Tag = ]") falls back to the name portion; if that is also
  // empty ("[Tag = ]") there is nothing to show.
  const name = match[1].trim();
  return name !== '' ? name : null;
}

/**
 * A registrar filter option: one formatted label plus every raw backend value
 * that collapses to it. Mirrors the {@link originDisplay} consolidation pattern
 * (group raw values under one display label; expand a selection back to all raw
 * values for the query) — but registrar groups are derived at runtime from the
 * data, not from a static id list.
 */
export interface RegistrarFilterGroup {
  /** Formatted display label — used as the dropdown option value/id. */
  value: string;
  /** All raw backend values that {@link formatRegistrar} maps to `value`. */
  rawValues: string[];
}

/**
 * Groups raw registrar/ISP values by their formatted display label so the
 * filter dropdown lists each label exactly once, matching the table cell.
 * A Nominet legacy row ("Gandi [Tag = GANDI]") and a newly-normalized row
 * ("GANDI") collapse into a single "GANDI" group; ISP strings (no tag notation)
 * pass through as singleton groups. Values that format to null (empty/blank) are
 * dropped. First-seen order is preserved for groups and for the raw values
 * within each group.
 *
 * Delegates to the generalized {@link buildMergedFilterGroups} with
 * {@link formatRegistrar} as the label function — grouping by label identity
 * is byte-identical to the original registrar-specific implementation.
 *
 * @param rawValues - Raw registrar/ISP values from the backend statistics
 * @returns One {@link RegistrarFilterGroup} per distinct formatted label
 */
export function buildRegistrarFilterGroups(
  rawValues: string[]
): RegistrarFilterGroup[] {
  return buildMergedFilterGroups(rawValues, formatRegistrar);
}

/**
 * Expands selected formatted registrar labels back to every raw backend value
 * in their group, so an IN-filter matches BOTH legacy "name [Tag = X]" rows and
 * newly-normalized "X" rows. This is the registrar analogue of
 * {@link getConsolidatedOriginIds} — applied at filter-apply time so the value
 * that reaches the backend query is always the set of raw stored values.
 *
 * A selected value with no matching group (e.g. a raw value restored from
 * storage, or a label whose rows have since disappeared) passes through
 * unchanged. The result is de-duplicated.
 *
 * Delegates to the generalized {@link expandMergedFilterValues} — the two
 * algorithms are line-for-line identical.
 *
 * @param selectedValues - Selected dropdown option values (formatted labels)
 * @param options - The registrar filter options, each carrying its `rawValues`
 * @returns The de-duplicated raw backend values to send to the query
 */
export function expandRegistrarFilterValues(
  selectedValues: string[],
  options: ReadonlyArray<{ value?: string | number; rawValues?: string[] }>
): string[] {
  return expandMergedFilterValues(selectedValues, options);
}

/**
 * Collapses stored raw registrar filter values back to their formatted labels
 * for dropdown selection display — the inverse of
 * {@link expandRegistrarFilterValues}, mirroring {@link collapseOriginValues}.
 * De-duplicated; a value that formats to null passes through unchanged.
 *
 * @param rawValues - Raw registrar values currently stored in the filter state
 * @returns The de-duplicated formatted labels to mark as selected
 */
export function collapseRegistrarFilterValues(rawValues: string[]): string[] {
  const labels = new Set<string>();
  for (const raw of rawValues) {
    labels.add(formatRegistrar(raw) ?? raw);
  }
  return Array.from(labels);
}

/**
 * Reconciles stored registrar filter values against the CURRENT registrar
 * options: collapses whatever is stored (legacy "name [Tag = X]", bare tag "X",
 * or a frozen mixed raw set) to formatted labels, re-expands those labels to
 * every raw backend value currently present in `options`, and returns the UNION
 * of the original stored values and that expansion (stored first, then new
 * expansion values, de-duplicated). This makes a persisted IN-filter match both
 * legacy and newly-normalized rows, and self-heal when new raw variants appear —
 * without the user re-applying the dropdown.
 *
 * Superset guarantee: because the original stored values are always retained,
 * the effective filter can only ever match MORE rows than the stored filter,
 * never fewer. The plain collapse→expand round-trip is lossy — when the current
 * options lack a stored raw's group (statistics-snapshot lag), it would drop
 * that raw entirely and the IN-query would match fewer rows than the pre-feature
 * behavior (e.g. legacy row "Gandi [Tag = GANDI]" would disappear). Keeping the
 * stored values in the IN-set fixes that: a stale raw value in the set is
 * harmless (it simply matches nothing), and this covers the stats-lag window
 * where a stored raw's group is absent from the current options.
 *
 * Callers may invoke this while `options` are still loading — with empty/stale
 * options the result degrades gracefully to just the stored values (never worse
 * than today's behavior). See the `filterOptionsStatus === 'success'` guard in
 * the Seeds orchestrator, which additionally defers reconciliation until options
 * are ready so newly-appeared raw variants are picked up.
 *
 * @param storedValues - Raw/legacy registrar values persisted in filter state
 * @param options - The current registrar filter options, each carrying `rawValues`
 * @returns The de-duplicated raw backend values to send to the query (a superset
 *   of `storedValues`)
 */
export function reconcileRegistrarFilterValues(
  storedValues: string[],
  options: ReadonlyArray<{ value?: string | number; rawValues?: string[] }>
): string[] {
  const expanded = expandRegistrarFilterValues(
    collapseRegistrarFilterValues(storedValues),
    options
  );
  return Array.from(new Set([...storedValues, ...expanded]));
}
